// Plan + dinner-event lifecycle (MEA-179). Pure client-side mirror of the
// server state machine in migration 027, plus the adapters that translate
// between the legacy plan JSONB, normalized dinner_events, and the board.
//
//   plan:   draft ⇄ in_review → locked → (revert) draft
//   event:  unplanned | draft | in_review | locked

export const PLAN_STATUS = { DRAFT: 'draft', IN_REVIEW: 'in_review', LOCKED: 'locked' };
export const EVENT_STATUS = { UNPLANNED: 'unplanned', DRAFT: 'draft', IN_REVIEW: 'in_review', LOCKED: 'locked' };
export const SOURCES = ['catalog', 'saved', 'ai', 'user', 'takeout', 'leftover', 'frozen'];

// Allowed plan transitions (matches lock_week / revert_week / submit_for_review).
const PLAN_TRANSITIONS = {
  draft: ['in_review', 'locked'],
  in_review: ['draft', 'locked'],
  locked: ['draft'], // revert only
};

export function canTransition(from, to) {
  return Boolean(PLAN_TRANSITIONS[from]?.includes(to));
}

// Throwing helper for callers that want a hard guard before an RPC.
export function assertTransition(from, to) {
  if (!canTransition(from, to)) {
    throw new Error(`Illegal plan transition: ${from} -> ${to}`);
  }
}

// How a single night's status changes when the whole week locks / reverts.
// Open nights stay open; a locked week doesn't invent meals.
export function eventStatusOnLock(status) {
  return status === EVENT_STATUS.DRAFT || status === EVENT_STATUS.IN_REVIEW ? EVENT_STATUS.LOCKED : status;
}
export function eventStatusOnRevert(status) {
  return status === EVENT_STATUS.LOCKED ? EVENT_STATUS.DRAFT : status;
}

// Board SourceChip speaks home/leftovers/frozen/takeout; map the richer
// candidate source onto it.
const SOURCE_TO_CHIP = {
  catalog: 'home', saved: 'home', ai: 'home', user: 'home',
  leftover: 'leftovers', frozen: 'frozen', takeout: 'takeout',
};
export function sourceToChip(source) {
  return SOURCE_TO_CHIP[source] || 'home';
}

// ── Date helpers (UTC, TZ-safe so tests are deterministic) ──────────────────
const DAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function addDaysISO(isoDate, n) {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

// ── Adapters ────────────────────────────────────────────────────────────────
// Legacy generated plan JSONB → dinner_events rows (dinner-first MVP). weekOf is
// the Monday ISO date. Only dinner slots are mapped; a missing/empty day yields
// no row (an open night is simply the absence of a planned event).
export function planToDinnerEvents(plan, { mealPlanId, householdId, weekOf }) {
  const rows = [];
  DAY_ORDER.forEach((day, i) => {
    const slot = plan?.[day]?.dinner;
    // Planning-first is one dinner per night (eng-review A2): a legacy split
    // adult/kid meal collapses to the adult recipe by design; the kid variant
    // is intentionally dropped, not lost silently.
    const recipeId = slot?.shared ? slot.recipeId : slot?.adultRecipeId;
    if (!recipeId) return;
    rows.push({
      meal_plan_id: mealPlanId,
      household_id: householdId,
      event_date: addDaysISO(weekOf, i),
      slot: 'dinner',
      recipe_id: recipeId,
      source: 'catalog',
      status: EVENT_STATUS.DRAFT,
    });
  });
  return rows;
}

// dinner_events rows → the seven-column board shape. recipeMap resolves titles
// and meta for candidates that reference a recipe.
export function dinnerEventsToBoard(events, { weekOf, recipeMap = {} } = {}) {
  const byDate = new Map(events.map((e) => [e.event_date, e]));
  return DAY_ORDER.map((day, i) => {
    const date = weekOf ? addDaysISO(weekOf, i) : null;
    const e = date ? byDate.get(date) : undefined;
    if (!e) return { day, date, empty: true };
    const recipe = e.recipe_id ? recipeMap[e.recipe_id] : null;
    return {
      day,
      date,
      isBusy: e.is_busy,
      note: e.note || null,
      meal: e.status === EVENT_STATUS.UNPLANNED
        ? null
        : {
            title: e.title || recipe?.name || 'Planned dinner',
            status: e.status,
            source: sourceToChip(e.source),
          },
    };
  });
}
